import React, { useState } from "react";
import { Box, Button, Card, Checkbox, Collapse, FormControlLabel, IconButton, List, ListItem, ListItemIcon, ListItemText, Popover, Typography } from "@mui/material";
import { Close, AttachFile, InfoRounded, PictureAsPdfRounded } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import imageCompression from "browser-image-compression";
import ImagePreviewPopup from "../Popup/ImagePreviewPopup";
import { TransitionGroup } from "react-transition-group";

const compressionOptions = {
    initialQuality: 0.6,
    useWebWorker: true,
};

function FileUploader(props) {
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            let newFiles = props.files.slice();
            newFiles = newFiles.concat(acceptedFiles.map((file) => Object.assign(file)));
            props.setFiles(newFiles);

            Promise.all(
                acceptedFiles.map(async (file) => {
                    if (!file.type.startsWith("image")) {
                        return Object.assign(file);
                    }
                    return imageCompression(file, compressionOptions);
                })
            ).then((files) => {
                newFiles = props.copiedFiles.slice().concat(files);
                props.setCopiedFiles(newFiles);

                let newFilesForCompression = props.filesForCompression.slice();
                newFilesForCompression = newFilesForCompression.concat(Array(acceptedFiles.length).fill(false));
                props.setFilesForCompression(newFilesForCompression);
            });
        },
    });

    const { t } = useTranslation("translation");

    const renderFilePreview = (file) => {
        if (file.type.startsWith("image")) {
            return (
                <img id={`fu_Img_preview_${file.name}`} width={38} height={38} alt={file.name} src={URL.createObjectURL(file)} onClick={() => handleClickImage(file)} style={{ cursor: "pointer" }} />
            );
        } else if (file.type.startsWith("application/pdf")) {
            return <PictureAsPdfRounded id={`fu_Pdf_preview_${file.name}`} fontSize="large" />;
        } else {
            return <AttachFile id={`fu_AttachFile_preview_${file.name}`} fontSize="large" />;
        }
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const isPopoverOpen = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const getIsOriginalCheckbox = (file, index) => {
        return file.type.startsWith("image") ? (
            <div sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <FormControlLabel
                    id={`fu_FormControlLabel_${index}`}
                    control={
                        <Checkbox
                            id={`fu_Checkbox_${index}`}
                            color="primary"
                            size="small"
                            checked={!!props.filesForCompression[index]}
                            onChange={() => {
                                const newValue = !props.filesForCompression[index];
                                props.setFilesForCompression([...props.filesForCompression.slice(0, index), newValue, ...props.filesForCompression.slice(index + 1)]);

                                let newCopiedFiles = props.copiedFiles.slice();
                                if (newValue) {
                                    newCopiedFiles[index] = props.files[index];
                                    props.setCopiedFiles(newCopiedFiles);
                                } else {
                                    imageCompression(props.files[index], compressionOptions).then((compressedFile) => {
                                        newCopiedFiles[index] = compressedFile;
                                        props.setCopiedFiles(newCopiedFiles);
                                    });
                                }
                            }}
                        />
                    }
                    label={
                        <Typography id={`fu_Typo_${index}`} fontSize={13}>
                            {t("Original")}
                        </Typography>
                    }
                />
                <IconButton id={`fu_IconButton_${index}`} color="primary" onClick={handleClick} size="small">
                    <InfoRounded id={`fu_InfoRounded_${index}`} fontSize="small" />
                </IconButton>
                <Popover
                    id={`fu_Popover_${index}`}
                    open={isPopoverOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                >
                    <Typography id={`fu_Typo_${index}`} sx={{ p: 2 }} fontSize={13}>
                        {t("OriginalInfo")}
                    </Typography>
                </Popover>
            </div>
        ) : null;
    };

    const handleRemoveFile = (index) => {
        props.setCopiedFiles((prev) => [...prev.filter((_item, ind) => ind !== index)]);
        props.setFiles((prev) => [...prev.filter((_item, ind) => ind !== index)]);
        props.setFilesForCompression((prev) => [...prev.filter((_item, ind) => ind !== index)]);
    };

    const getFileSize = (file) => {
        if (Math.round(file.size / 100) / 10 > 1000) {
            return `${(Math.round(file.size / 100) / 10000).toFixed(1)} MB`;
        } else {
            return `${(Math.round(file.size / 100) / 10).toFixed(1)} KB`;
        }
    };

    const fileList = props.copiedFiles.map((file, index) => (
        <Collapse key={file.name}>
            <Card id={`fu_Card_${index}`} elevation={2} sx={{ marginBottom: 2, border: "1px solid #e0e0e0" }}>
                <ListItem
                    id={`fu_ListItem_${index}`}
                    secondaryAction={
                        <IconButton id={`fu_IconButton_${index}`} onClick={() => handleRemoveFile(index)}>
                            <Close id={`fu_Close_${index}`} />
                        </IconButton>
                    }
                >
                    <ListItemIcon id={`fu_ListItemIcon_${index}`}>{renderFilePreview(file)}</ListItemIcon>
                    <ListItemText
                        id={`fu_ListItemText_${index}`}
                        primary={file.name}
                        secondary={
                            <React.Fragment>
                                {getFileSize(file)}
                                {getIsOriginalCheckbox(file, index)}
                            </React.Fragment>
                        }
                        primaryTypographyProps={{
                            style: {
                                wordBreak: "break-all",
                            },
                        }}
                    />
                </ListItem>
            </Card>
        </Collapse>
    ));

    const handleRemoveAllFiles = () => {
        props.setFiles([]);
        props.setCopiedFiles([]);
        props.setFilesForCompression([]);
    };

    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState();
    const handleClickImage = (image) => {
        setPreviewImage(image);
        setIsImagePreviewOpen(true);
    };

    return (
        <React.Fragment>
            <div {...getRootProps({ className: "dropzone" })}>
                <input {...getInputProps()} />
                <Box
                    id="fu_Box"
                    sx={{
                        display: "flex",
                        flexDirection: ["column", "column", "row"],
                        alignItems: "center",
                        justifyContent: "space-around",
                        border: "2px dashed #e4e4e4",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    <img id="fu_img" alt="Upload" src={require("../../assets/upload.png")} />
                    <Box id="fu_Box_2" sx={{ display: "flex", flexDirection: "column", textAlign: ["center", "center", "inherit"] }}>
                        <Typography id="fu_Typo" sx={{ marginBottom: 4 }} variant="h5">
                            {t("ChooseFile")}
                        </Typography>
                        <Typography id="fu_Typo_2" color="textSecondary">
                            {t("DropFilesHereOrClickToUpload")}
                        </Typography>
                    </Box>
                </Box>
            </div>
            {props.copiedFiles.length ? (
                <React.Fragment>
                    <List id="fu_List" sx={{ marginTop: 2, marginBottom: 2 }}>
                        <TransitionGroup>{fileList}</TransitionGroup>
                        <div style={{ display: "flex", justifyContent: "right", alignItems: "right", paddingRight: 20, paddingBottom: 10 }}>
                            <Button id="fu_Button" color="secondary" variant="contained" onClick={handleRemoveAllFiles}>
                                {t("RemoveAll")}
                            </Button>
                        </div>
                    </List>
                </React.Fragment>
            ) : null}

            {isImagePreviewOpen ? <ImagePreviewPopup isImagePreviewOpen={isImagePreviewOpen} previewImage={previewImage} handleClose={() => setIsImagePreviewOpen(false)} /> : null}
        </React.Fragment>
    );
}

export default FileUploader;
